import type { Car, CarStations } from '@/entities/cars/car.entity';
import type { CarFormValues } from '../EditCarForm.vue';

const mapStations = (stations: CarStations[]) =>
  stations
    .map(({ station, fromDate, untilDate, primary }) => ({
      id: station.id,
      fromDate,
      untilDate,
      primary: primary !== false,
    }))
    .sort((a, b) => {
      const aDate = a.fromDate ? new Date(a.fromDate).getTime() : 0;
      const bDate = b.fromDate ? new Date(b.fromDate).getTime() : 0;
      return aDate > bDate ? 1 : -1;
    });

const mapLegalEntities = (car: Car) =>
  car.legalEntities?.length
    ? [...car.legalEntities]
        .sort(
          (a, b) =>
            (a.fromDate ? new Date(a.fromDate).getTime() : 0) -
            (b.fromDate ? new Date(b.fromDate).getTime() : 0),
        )
        .map(({ legalEntity, fromDate }) => ({ legalEntityId: legalEntity.id, fromDate }))
    : undefined;

export const getInitialValues = (car: Car): CarFormValues => {
  if (!car.isGhostCar) {
    return {
      vehicleTypeId: car.vehicleType.id,
      licencePlate: car.licencePlate,
      fin: car.fin,
      tyres: car.tyres,
      selectedImageFile: undefined,
      availableFrom: car.availableFrom,
      availableUntil: car.availableUntil,
      hiddenFromPublic: car.hiddenFromPublic,
      notAtTheHub: car.notAtTheHub,
      stations: mapStations(car.stations.filter((s) => s.primary !== false)),
      additionalStations: mapStations(car.stations.filter((s) => s.primary === false)),
      legalEntities: mapLegalEntities(car),
      acquisitionType: car.acquisitionType,
      purchasePrice: car.purchasePrice,
      leasingRate: car.leasingRate,
      listPrice: car.listPrice,
      color: car.color,
      insuranceCompany: car.insuranceCompany,
      firstRegistration: car.firstRegistration,
      salePrice: car.salePrice,
      saleDate: car.saleDate,
      fuelType: car.fuelType,
      transmission: car.transmission,
      drive: car.drive,
      batteryCapacityInKwh: car.batteryCapacityInKwh,
      tankVolumeInLiter: car.tankVolumeInLiter,
      rangeInKm: car.rangeInKm,
      seats: car.seats,
      additionalSeats: car.additionalSeats,
      power: car.power,
      accelaration: car.accelaration,
      hasNavigation: car.hasNavigation,
      hasDistanceControl: car.hasDistanceControl,
      hasRearViewCamera: car.hasRearViewCamera,
      hasTowHitch: car.hasTowHitch,
      brakedTowingCapacityInKg: car.brakedTowingCapacityInKg,
      unbrakedTowingCapacityInKg: car.unbrakedTowingCapacityInKg,
      promotionalVideoMediaId: car.promotionalVideo?.id ?? null,
    };
  } else {
    return {
      vehicleTypeId: car.vehicleType.id,
      licencePlate: car.licencePlate,
      selectedImageFile: undefined,
      hiddenFromPublic: car.hiddenFromPublic,
      notAtTheHub: car.notAtTheHub,
      stations: mapStations(car.stations.filter((s) => s.primary !== false)),
      additionalStations: mapStations(car.stations.filter((s) => s.primary === false)),
      legalEntities: mapLegalEntities(car),
      possibleUpgradeCars: [...car.possibleUpgradesTo]
        .sort((a, b) => a.order - b.order)
        .map((upgrade) => upgrade.to),
      isMysteryCar: car.isMysteryCar,
      color: car.color,
      fuelType: car.fuelType,
      transmission: car.transmission,
      drive: car.drive,
      batteryCapacityInKwh: car.batteryCapacityInKwh,
      tankVolumeInLiter: car.tankVolumeInLiter,
      rangeInKm: car.rangeInKm,
      seats: car.seats,
      additionalSeats: car.additionalSeats,
      power: car.power,
      accelaration: car.accelaration,
      hasNavigation: car.hasNavigation,
      hasDistanceControl: car.hasDistanceControl,
      hasRearViewCamera: car.hasRearViewCamera,
      hasTowHitch: car.hasTowHitch,
      brakedTowingCapacityInKg: car.brakedTowingCapacityInKg,
      unbrakedTowingCapacityInKg: car.unbrakedTowingCapacityInKg,
      promotionalVideoMediaId: car.promotionalVideo?.id ?? null,
    };
  }
};
